import { FormikHelpers } from 'formik'
import { useLucaSignUp } from '../hooks/useLucaSignUp'
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth'
import { auth } from '../../../../../firebase'
import { useMutationCreateUser } from '../queries/userQueries'
import { useLucaAuth } from '../hooks/useAuth'
import { FC, useState } from 'react'
import { sileo } from 'sileo'
import { useSwiper } from 'swiper/react'
import { UserAuth } from '../../../../../context/AuthContext'
import { BaseFormCreateUser, Step1Values } from '../../components/BaseFormCreateUser'
import { useGlobalContext } from '../../../../../context/GlobalState'
import { REGISTRATION_STATUS } from '../../../../../utils/authSession'
import { pushGtmEvent } from '../../../../../utils/gtm'

const authActionBaseUrls: Record<string, string> = {
  production: 'https://gcp-prod-465219.web.app',
  beta: 'https://gcp-beta-503317.web.app',
  development: 'https://gcp-dev-465219.web.app'
}

const getAuthActionBaseUrl = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || 'development'
  return authActionBaseUrls[env] || window.location.origin
}

const getEmailVerificationActionSettings = () => ({
  url: `${getAuthActionBaseUrl()}/auth/action`,
  handleCodeInApp: true
})

const getFirebaseErrorCode = (error: unknown) => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return String((error as { code?: unknown }).code || '')
  }

  return ''
}

export const FormCreateUserAccount: FC = () => {
  const { ui } = useGlobalContext()
  const addAccountProfileValues = useLucaSignUp(state => state.setAccountProfile)
  const accountProfile = useLucaSignUp(state => state.accountProfile)
  const userMutation = useMutationCreateUser()
  const setApiToken = useLucaAuth(state => state.setApiToken)
  const deleteApiToken = useLucaAuth(state => state.deleteApiToken)
  const [isLoading, setIsLoading] = useState(false)
  const swiper = useSwiper()
  const {
    user,
    setBackendSession,
    beginFirebaseRegistration,
    endFirebaseRegistration
  } = UserAuth()
  const isExistingFirebaseUser = Boolean(user)

  const step1InitialValues: Step1Values = {
    firstname: accountProfile?.firstname || '',
    lastname: accountProfile?.lastname || '',
    phoneNumber: accountProfile?.phoneNumber || '',
    email: accountProfile?.email || user?.email || '',
    password: '',
  }

  const handleSlideNextStep = async (values: Step1Values, actions: FormikHelpers<Step1Values>) => {
    setIsLoading(true)
    pushGtmEvent({ event: 'user_registration_attempt' })
    const formValues = {
      ...values,
      phoneNumber: values.phoneNumber?.startsWith('+')
        ? values.phoneNumber.slice(1)
        : values.phoneNumber
    }
    try {
      ui.setLoader({ visible: true, text: 'Creando Usuario' })
      beginFirebaseRegistration()
      const createdFirebaseUser = await handleCreateUserAccount(formValues)

      sileo.success({
        title: createdFirebaseUser ? 'Correo Enviado' : 'Registro Recuperado',
        description: (
          <span className='text-white font-medium'>
            {createdFirebaseUser
              ? 'Revisa tu correo para completar el registro 👌'
              : 'Tus datos fueron guardados. Continúa con la configuración.'}
          </span>
        )
      })
      addAccountProfileValues(formValues)
      actions.setSubmitting(true)
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[Auth][register-error]', {
          code: getFirebaseErrorCode(error),
          message: error instanceof Error ? error.message : String(error),
          email: formValues.email.trim().toLowerCase(),
          firebaseProjectId: auth.app.options.projectId
        })
      }
      actions.setFieldError(
        'email',
        getFirebaseErrorCode(error) === 'auth/email-already-in-use'
          ? 'Este correo ya está registrado. Inicia sesión para retomar el registro.'
          : 'No se pudo completar el registro. Intenta nuevamente.'
      )
      deleteApiToken()
    } finally {
      endFirebaseRegistration()
      ui.setLoader({ visible: false, text: '' })
      setIsLoading(false)
    }
  }

  const handleCreateUserAccount = async (values: Step1Values) => {
    let firebaseUser = user
    let createdFirebaseUser = false

    if (!firebaseUser) {
      if (process.env.NODE_ENV !== 'production') {
        console.info('[Auth][firebase-register]', {
          email: values.email.trim().toLowerCase(),
          firebaseProjectId: auth.app.options.projectId,
          firebaseAuthDomain: auth.app.options.authDomain
        })
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        values?.email,
        values.password
      )
      firebaseUser = userCredential.user
      createdFirebaseUser = true
    }

    const firebaseToken = await firebaseUser.getIdToken(true)
    const userPayload = {
      token: firebaseToken,
      firstname: values.firstname,
      lastname: values.lastname,
      phoneNumber: values.phoneNumber,
      email: values.email
    }
    if (createdFirebaseUser) {
      localStorage.removeItem('isValidated')
      localStorage.setItem('firebaseVerificationCredential', JSON.stringify({
        uid: firebaseUser.uid,
        email: firebaseUser.email || values.email
      }))
      await sendEmailVerification(firebaseUser, getEmailVerificationActionSettings())
    }

    const userData = await userMutation.mutateAsync(userPayload)
    pushGtmEvent({ event: 'user_registered' })
    if (userData.token) {
      setApiToken(userData.token)
    }
    setBackendSession(userData, REGISTRATION_STATUS.ORGANIZATION_PENDING, firebaseUser)
    swiper.slideNext()
    return createdFirebaseUser
  }

  return (
    <BaseFormCreateUser
      title=''
      subtitle=''
      initialValues={step1InitialValues}
      onSubmit={handleSlideNextStep}
      isLoading={isLoading}
      isExistingFirebaseUser={isExistingFirebaseUser}
    />
  )
  }
